import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * CityAutocomplete - Componente de autocompletado de municipios españoles
 * Usa datos locales del IGN/GeoNames (no requiere API key)
 */
const CityAutocomplete = ({ name, value, onChange, placeholder = 'Buscar municipio...' }) => {
  const [inputValue, setInputValue] = useState(value?.text || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value?.text !== undefined && value.text !== inputValue) {
      setInputValue(value.text);
    }
  }, [value?.text]);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Usar endpoint de geocode que combina municipios locales + Nominatim para calles y direcciones
      const params = new URLSearchParams({ q: query, limit: '8' });
      const response = await fetch(`${API_URL}/api/geocode?${params}`);
      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        const mapped = data.data.map(m => ({
          name: m.name || m.display_name?.split(',')[0] || '',
          city: m.provincia || m.display_name?.split(',').slice(1, 2).join('').trim() || '',
          country: m.country === 'AD' ? 'Andorra' : m.country === 'FR' ? 'Francia' : m.country === 'PT' ? 'Portugal' : (m.type === 'municipality' ? 'España' : ''),
          coordinates: m.coordinates,
          displayName: m.display_name || m.name,
          type: m.type,
        }));
        setSuggestions(mapped);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        if (!data.success) {
          setError(data.error?.message || 'Error al buscar ubicaciones');
        }
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Error de conexión');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);

    onChange({
      text: newValue,
      coordinates: null
    });

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 400);
  };

  const handleSelectSuggestion = (suggestion) => {
    // For street-level results (from Nominatim), use displayName; for municipalities, build display text
    const displayText = suggestion.type !== 'municipality' && suggestion.displayName
      ? suggestion.displayName
      : [suggestion.name, suggestion.city, suggestion.country].filter(Boolean).join(', ');

    setInputValue(displayText);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);

    onChange({
      text: displayText,
      coordinates: suggestion.coordinates
    });
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputRef.current && !inputRef.current.contains(e.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const styles = {
    container: { position: 'relative', width: '100%' },
    input: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      border: '1px solid #d0d6e4',
      borderRadius: '8px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    loading: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '12px',
      color: '#666'
    },
    suggestionsList: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      margin: 0,
      padding: 0,
      listStyle: 'none',
      backgroundColor: '#fff',
      border: '1px solid #d0d6e4',
      borderTop: 'none',
      borderRadius: '0 0 8px 8px',
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    suggestionItem: (isHighlighted) => ({
      padding: '10px 12px',
      cursor: 'pointer',
      backgroundColor: isHighlighted ? '#e8f0fe' : '#fff',
      borderBottom: '1px solid #eee',
      transition: 'background-color 0.1s'
    }),
    suggestionName: { fontWeight: 500, color: '#1d2433' },
    suggestionMeta: { fontSize: '12px', color: '#5a6478', marginTop: '2px' },
    error: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      padding: '10px 12px',
      backgroundColor: '#fee',
      border: '1px solid #fcc',
      borderTop: 'none',
      borderRadius: '0 0 8px 8px',
      color: '#c00',
      fontSize: '14px',
      zIndex: 1000,
    },
    coordsBadge: {
      display: 'inline-block',
      marginTop: '4px',
      padding: '2px 6px',
      backgroundColor: '#d6f5e6',
      borderRadius: '4px',
      fontSize: '10px',
      color: '#14603c'
    }
  };

  return (
    <div style={styles.container}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        autoComplete="off"
        style={styles.input}
      />

      {isLoading && <div style={styles.loading}>Buscando...</div>}

      {showSuggestions && suggestions.length > 0 && (
        <ul ref={suggestionsRef} style={styles.suggestionsList}>
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.coordinates?.lat}-${suggestion.coordinates?.lng}-${index}`}
              onClick={() => handleSelectSuggestion(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
              style={styles.suggestionItem(index === highlightedIndex)}
            >
              <div style={styles.suggestionName}>{suggestion.name}</div>
              {(suggestion.city || suggestion.country) && (
                <div style={styles.suggestionMeta}>
                  {[suggestion.city, suggestion.country].filter(Boolean).join(', ')}
                </div>
              )}
              {suggestion.coordinates && (
                <span style={styles.coordsBadge}>
                  {suggestion.coordinates.lat.toFixed(4)}, {suggestion.coordinates.lng.toFixed(4)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

export default CityAutocomplete;
